import React, { useState, useRef, useEffect, useCallback } from 'react';
import svgPanZoom from 'svg-pan-zoom/src/svg-pan-zoom.js';
import { createPopper } from '@popperjs/core';
import './App.scss';
import CategoryOverview from './CategoryOverview/CategoryOverview';
import NavButtons from './NavButtons/NavButtons';
import Container from './Map/Container';
import logo from '../assets/logo.png';
import compareCircle from '../assets/compare_circle.svg';
import dataByArea from '../dataByArea.js';
import AreaDetail from './AreaDetail/AreaDetail';
import StateDetail from './AreaDetail/StateDetail';
import Compare from './Compare/Compare';
import categoryData from '../categoryData.js';
import ColorKeys from './ColorKeys/ColorKeys';
import hamburger from '../assets/hamburger.svg';
import colorKeyContent from '../colorKeyContent.js';
import AreaDetailMobile from './AreaDetail/AreaDetailMobile';
import SelectState from './SelectState/SelectState';
import areaNames from '../areaNames.js';
import NavRight from './NavRight/NavRight';
import Hamburger from './Hamburger/Hamburger';
import HowToPopUp from './Intro/HowToIPopUp';
import SelectStateNav from './SelectState/SelectStateNav';
import SelectStateButton from './SelectState/SelectStateButton';
import Header from '../components/common-header/Header';
import nationalAverage from '../nationalAverage';
import plus from '../assets/plus.svg';

const ntlSup = nationalAverage.supply;
const ntlMedPrice = nationalAverage.medianPrice;
const ntlYoy = nationalAverage.yoyChange;
const ntlAffor = nationalAverage.affordability;
const ntlPop = nationalAverage.popChange;

function App() {
  const [currentCategory, setCurrentCategory] = useState(categoryData.intro);
  const [currentData, setCurrentData] = useState();
  const [colorCode, setColorCode] = useState({});
  const [currentArea, setCurrentArea] = useState();
  const [currentClickedArea, setCurrentClickedArea] = useState();
  const [buttonActive, setButtonActive] = useState('intro');
  const [savedData, setSavedData] = useState({});
  const [savedDataArray, setSavedDataArray] = useState(Object.keys(savedData));
  const [currentWidth, setCurrentWidth] = useState(window.innerWidth);
  const [zoomed, setZoomed] = useState(false);
  const [checked, setChecked] = useState({});
  const [showCompare, setShowCompare] = useState(false);
  const [showAreaDetail, setShowAreaDetail] = useState(false);
  const [showHowToInfo, setShowHowToInfo] = useState(false);
  const [currentState, setCurrentState] = useState(null);
  const [currentStateName, setCurrentStateName] = useState(null);
  const [previousStateName, setPreviousStateName] = useState(null);
  const [nextStateName, setNextStateName] = useState(null);
  const [showStates, setShowStates] = useState(false);
  const [showHamburger, setShowHamburger] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [selectedStateIntro, setSelectedStateIntro] = useState(null);
  const [showStateDetail, setShowStateDetail] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [collapse, setCollapse] = useState(false);
  const [currentStateIndex, setCurrentStateIndex] = useState(null);
  const [animationDone, setAnimationDone] = useState(false);

  const zoom = useRef(null);
  const size = useRef();
  const overviewCopy = useRef();
  const checkbox = useRef();
  const northAm = useRef();
  const frame = useRef();
  const filler = useRef();
  const selectState = useRef();
  const hamburgerRef = useRef();
  const introText = useRef();
  const logoRef = useRef();
  const rightNav = useRef();
  const topNav = useRef();
  const howtoRef = useRef();
  const stateLines = useRef();
  const mapContainer = useRef();
  const areaDetailRef = useRef();
  const stateMap = useRef();
  const overview = useRef();
  const exploreTheStates = useRef();
  const selectStateButtonRef = useRef();
  const supplyRef = useRef();
  const appRef = useRef();
  const affRef = useRef();
  const forecastRef = useRef();
  const plusRef = useRef();
  const collapseRef = useRef();
  const disclaimerRef = useRef();
  const logoTooltip = useRef();

  const handleClickCollapse = useCallback(() => {
    if (collapse) {
      overview.current.style.transform = 'translateX(0)';
      collapseRef.current.style.animation = 'rotateReverse .7s ease-in 0.5s';
      setCollapse(false);
      setTimeout(() => {
        plusRef.current.style.visibility = 'hidden';
      }, 300);
    } else {
      overview.current.style.transform = 'translateX(-500px)';
      collapseRef.current.style.animation = 'rotate .7s ease-in';
      plusRef.current.style.animation = '';
      setCollapse(true);
      setTimeout(() => {
        plusRef.current.style.visibility = 'visible';
      }, 700);
    }
    if (overview.current.style.backgroundColor === '#FFFFFF') {
      overview.current.style.backgroundColor = 'rgba(255,255,255,.52)';
    }
  }, [collapse]);

  //click handler for reset button
  const handleReset = useCallback(() => {
    setShowAreaDetail(false);
    setShowStateDetail(false);
    setSelectedStateIntro(null);
    setCurrentClickedArea(null);
    setShowOverview(true);
    if (collapse) {
      handleClickCollapse();
    }

    if (showStates) {
      handleClickSelectState('close');
    }

    if (!mobileView && zoom.current) {
      zoom.current.fit();
      zoom.current.center();
    }
    setTimeout(() => {
      if (filler.current) {
        filler.current.style.width = '10%';
      }
      if (!mobileView && zoom.current) {
        zoom.current.resize();
      }
      setZoomed(false);
      if (overview.current) {
        overview.current.style.opacity = '1';
      }
      if (stateLines) {
        stateLines.current.style.strokeWidth = '0.3';
      }
    }, 50);
  }, [collapse, handleClickCollapse, mobileView, showStates]);

  //updates the screen to new category
  const updateToNewCategory = useCallback(
    (button) => {
      setShowStateDetail(false);

      if (showOverview) {
        overview.current.style.opacity = '0';
      }

      setTimeout(() => {
        setZoomed(false);
        setCurrentCategory(categoryData[button]);
        if (showOverview && overview.current) {
          overview.current.style.opacity = '1';
        }
      }, 700);

      if (showAreaDetail) {
        setShowAreaDetail(false);
      }

      if (button === 'intro') {
        if (northAm.current) {
          northAm.current.style.opacity = '0';
        }

        setTimeout(() => {
          if (animationDone) {
            stateMap.current.style.animation = 'fadeIn 0.7s ease-in forwards';
            stateMap.current.style.zIndex = '5';
          }
        }, 500);
      } else {
        if (northAm.current) {
          northAm.current.style.opacity = '0.5';
          setTimeout(() => {
            northAm.current.style.opacity = '1';
          }, 800);
        }
      }

      mapContainer.current.style.opacity = '1';
      let color = {};
      if (button === 'supply') {
        dataByArea.forEach((data) => {
          let job = Number(data.job);
          if (job < 0) {
            color[data.id] = 'jobloss';
          } else if (job >= 0 && job < 3) {
            color[data.id] = 'sufficient';
          } else if (job >= 3 && job < 100) {
            color[data.id] = 'shortage';
          } else if (job >= 100) {
            color[data.id] = 'highShortage';
          }
        });
      } else if (button === 'appreciation') {
        dataByArea.forEach((data) => {
          let yoy = Number(data.yoyChange);
          if (yoy < 0) {
            color[data.id] = 'loss';
          } else if (yoy > 0 && yoy < 16) {
            color[data.id] = 'growth1';
          } else if (yoy >= 16 && yoy < 26) {
            color[data.id] = 'growth2';
          } else if (yoy >= 26 && yoy < 36) {
            color[data.id] = 'growth3';
          } else if (yoy >= 36) {
            color[data.id] = 'growth4';
          } else if (yoy === 'na') {
            color[data.id] = 'nodata';
          }
        });
      } else if (button === 'affordability') {
        dataByArea.forEach((data) => {
          let hai = Number(data.hai);
          if (hai < 100) {
            color[data.id] = 'unaffordable';
          } else if (hai >= 100 && hai < 151) {
            color[data.id] = 'affordable';
          } else if (hai >= 151 && hai < 301) {
            color[data.id] = 'vAffordable';
          } else if (hai >= 301) {
            color[data.id] = 'eAffordable';
          } else if (Number.isNaN(hai)) {
            color[data.id] = 'nodata';
          }
        });
      } else if (button === 'forecast') {
        dataByArea.forEach((data) => {
          let growth = Number(data.growth);
          if (growth < 0) {
            color[data.id] = 'drop';
          } else if (growth >= 0 && growth < 11) {
            color[data.id] = 'growth10';
          } else if (growth >= 11 && growth < 21) {
            color[data.id] = 'growth20';
          } else if (growth >= 21 && growth < 31) {
            color[data.id] = 'growth30';
          } else if (Number.isNaN(growth)) {
            color[data.id] = 'nodata';
          } else if (growth >= 31) {
            color[data.id] = 'growth40';
          }
        });
      }

      setColorCode(color);
      setButtonActive(button);
    },
    [showAreaDetail, showOverview, animationDone]
  );
  //click handler for nav buttons at top
  const handleClickNavButtons = useCallback(
    (button) => {
      setSelectedStateIntro(null);

      setTimeout(() => {
        setShowOverview(true);
        handleReset();
        setCurrentState(null);
        updateToNewCategory(button);
      }, 700);
    },
    [handleReset, updateToNewCategory]
  );

  //when zoomed on the map
  const onZoom = (e) => {
    if (filler.current) {
      filler.current.style.width = '0';
    }
    if (stateLines.current) {
      stateLines.current.style.strokeWidth = '0.6';
    }
    setZoomed(true);
  };

  //click handler for clicking an area on the map
  const handleClickMetroArea = (e) => {
    const distanceY = currentWidth > 1050 ? 0.7 : 0.8;
    if (!mobileView) {
      const fips = e.target.className.baseVal.slice(5, 10);
      setCurrentClickedArea(currentData[fips]);
      if (savedData[fips]) {
        setChecked(true);
      } else if (savedData[fips] === undefined) {
        setChecked(false);
      }
      if (overview.current && showOverview) {
        overview.current.style.opacity = '0';
      }
      setShowAreaDetail(true);
      setShowStateDetail(false);
      let currentLocX = e.clientX;
      let currentLocY = e.clientY;
      let currentSubtractX = zoom.current.getSizes().width * 0.8;
      let currentSubtractY = zoom.current.getSizes().height * distanceY;
      if (!zoomed) {
        zoom.current.pan({
          x: -(currentLocX - currentSubtractX),
          y: -(currentLocY - currentSubtractY)
        });
      }
      setTimeout(() => {
        setShowOverview(false);
        setCollapse(false);
        zoom.current.zoomBy(2);
      }, 50);
    }
  };

  const handleClickIntroState = (e) => {
    const distanceY = currentWidth > 1050 ? 0.7 : 0.8;
    let currentLocX = e.clientX;
    let currentLocY = e.clientY;
    let currentSubtractX = zoom.current.getSizes().width * 0.8;
    let currentSubtractY = zoom.current.getSizes().height * distanceY;
    let state = e.target.textContent;
    updateToNewCategory('supply');

    setSelectedStateIntro(state);
    overview.current.style.opacity = '0';
    setTimeout(() => {
      if (!zoomed) {
        zoom.current.pan({
          x: -(currentLocX - currentSubtractX),
          y: -(currentLocY - currentSubtractY)
        });
      }
    }, 100);
    if (state === 'Alaska' || state === 'Montana' || state === 'Wyoming') {
      setShowStateDetail(true);
    }
    setTimeout(() => {
      zoom.current.zoomBy(2);

      setShowOverview(false);
    }, 150);
    setTimeout(() => {
      onZoom();
    }, 750);
  };
  //click handler for hamburger menu for mobile
  const handleClickHamburger = (state) => {
    if (hamburgerRef.current) {
      if (state === 'close') {
        hamburgerRef.current.style.animation = 'slideLeft .5s ease-in forwards';
        introText.current.style.animation = 'textAlign 1.5s ease-in';
        setShowHamburger(false);
      } else {
        hamburgerRef.current.style.animation = 'slideRight 0.5s ease-in forwards';
        introText.current.style.animation = '';
        setShowHamburger(true);
      }
    }
  };

  //click handler for closing how use use
  const handleClickClose = () => {
    howtoRef.current.style.animation = 'fadeOut 0.5s ease-out forwards';

    setTimeout(() => {
      setShowHowToInfo(false);
    }, 500);
    if (exploreTheStates.current.style.animation === '') {
      exploreTheStates.current.style.animation = 'type 1.5s ease-in forwards';
      overview.current.style.animation = 'overviewBackground 2s ease-in 1.5s forwards';
      supplyRef.current.style.animation = 'active-housingShortage 1.5s ease-out 1s';
      appRef.current.style.animation = 'active-homePrice 1.5s ease-out 1.2s';
      affRef.current.style.animation = 'active-affordability 1.5s ease-out 1.4s';
      forecastRef.current.style.animation = 'active-hosingDemand 1.5s ease-out 1.6s';
    }
  };

  //click handler for how to use the map icon
  const handleClickI = () => {
    setShowHowToInfo(true);
    setTimeout(() => {
      howtoRef.current.style.animation = 'fadeIn .7s ease-in forwards';
    }, 300);
  };

  //selecting state for mobile
  const handleClickArea = (name) => {
    const names = Object.keys(areaNames);
    const index = names.indexOf(name);
    const data = areaNames[name].map((metro) => {
      return currentData[metro];
    });
    setCurrentStateIndex(index);
    setCurrentStateName(name);
    setShowOverview(false);
    setPreviousStateName(names[index - 1] ? names[index - 1] : names[names.length - 1]);
    setNextStateName(names[index + 1] ? names[index + 1] : names[0]);
    setCurrentState(data);
    handleClickSelectState('close');
  };

  const handleClickSelectState = (state) => {
    if (state === 'close') {
      setShowStates(false);
      selectState.current.style.animation = 'slideDown 1s ease-in forwards';
    } else {
      setShowStates(true);
      selectState.current.style.animation = 'slideUp 1s ease-in forwards';
    }
  };

  //setting up the pan limit
  const beforePan = function (oldPan, newPan) {
    const gutterWidth = 100;
    const gutterHeight = 100;
    // Computed variables
    const sizes = this.getSizes();
    const leftLimit = -((sizes.viewBox.x + sizes.viewBox.width) * sizes.realZoom) + gutterWidth;
    const rightLimit = sizes.width - gutterWidth - sizes.viewBox.x * sizes.realZoom;
    const topLimit = -((sizes.viewBox.y + sizes.viewBox.height) * sizes.realZoom) + gutterHeight;
    const bottomLimit = sizes.height - gutterHeight - sizes.viewBox.y * sizes.realZoom;

    const customPan = {};
    customPan.x = Math.max(leftLimit, Math.min(rightLimit, newPan.x));
    customPan.y = Math.max(topLimit, Math.min(bottomLimit, newPan.y));

    return customPan;
  };

  const handleMouseEnterLogo = (e) => {
    const content = logoTooltip.current;
    createPopper(e.target, content, {
      placement: 'bottom',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 5]
          }
        }
      ]
    });
    content.style.visibility = 'visible';
  };

  const handleMouseLeaveLogo = (e) => {
    logoTooltip.current.style.visibility = 'hidden';
  };

  //set currentData, mobileView
  useEffect(() => {
    let width = window.innerWidth;
    let data = {};
    dataByArea.forEach((e) => {
      data[e.id] = {
        fips: e.id,
        state: e.state,
        abb: e.abbState,
        name: e.name,
        job: e.job,
        supply: e.supply,
        yoyChange: e.yoyChange,
        medianPrice: e.medianPrice,
        hai: e.hai,
        growth: e.growth
      };
    });
    setCurrentData(data);
    if (width > 767) {
      setMobileView(false);
    } else {
      setMobileView(true);
    }
  }, []);

  //setup/reset panzoom library if it's not mobile view

  useEffect(() => {
    if (mobileView === false || (mobileView && currentWidth > 767)) {
      if (northAm.current !== null && !zoom.current) {
        if (animationDone) {
          handleClickNavButtons(buttonActive);
        }
        zoom.current = svgPanZoom('#svg9559', {
          minZoom: 1,
          maxZoom: 2,
          fit: 1,
          center: true,
          contain: true,
          dblClickZoomEnabled: false,
          beforePan: beforePan,
          onZoom: onZoom
        });
      }
    } else if (mobileView && zoom.current) {
      setTimeout(() => {
        if (zoom.current !== null) {
          zoom.current.fit();
          zoom.current.center();
          zoom.current.resize();
          zoom.current.destroy();
          zoom.current = null;
        }
      }, 500);
    }
  }, [mobileView, currentWidth, buttonActive, handleClickNavButtons, animationDone]);

  //setup resize observer
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (width !== currentWidth) {
        setCurrentWidth(width);

        if (width > 767 && zoom.current !== null) {
          zoom.current.resize();
          zoom.current.fit();
          zoom.current.center();
          if (overview.current) {
            overview.current.style.animation = 'overviewBackground 2s ease-in forwards';
          }

          if (currentState) {
            setCurrentState(null);
          }
        }

        if (width > 767 && mobileView) {
          setMobileView(false);
          handleReset();
          if (currentState) {
            setCurrentState(null);
          }
        }
        if (currentState === null && !mobileView && width < 768) {
          if (zoom.current !== null) {
            zoom.current.resize();
            zoom.current.fit();
            zoom.current.center();
          }

          setMobileView(true);
          if (showAreaDetail) {
            setShowAreaDetail(false);
          }
          if (zoomed) {
            setZoomed(false);
          }
        }
      }
    });
    resizeObserver.observe(size.current);
  }, [currentState, currentWidth, mobileView, showAreaDetail, zoomed, handleReset]);

  //animation in the beginning
  useEffect(() => {
    overview.current.style.backgroundColor = '#FFFFFF';

    logoRef.current.style.animation = 'fadeIn .7s ease-in forwards';
    topNav.current.style.animation = 'fadeIn .7s ease-in .5s forwards';
    rightNav.current.style.animation = 'fadeIn .7s ease-in 1s forwards';
    stateMap.current.style.animation = 'fadeIn .7s ease-in 1.5s forwards';
    mapContainer.current.style.animation = 'fadeIn .7s ease-in 1.5s forwards';
    setTimeout(() => {
      setAnimationDone(true);
      overview.current.style.opacity = '1';
    }, 1500);
    setTimeout(() => {
      setShowHowToInfo(true);
    }, 2500);

    if (selectStateButtonRef.current) {
      selectStateButtonRef.current.style.animation = 'fadeIn .7s ease-in 2s forwards';
    }
  }, []);

  return (
    <section className="App" ref={size}>
      <Header clientName={"National Association of REALTORS®"}/>
      <Compare
        showCompare={showCompare}
        setShowCompare={setShowCompare}
        currentData={currentData}
        savedData={savedData}
        setSavedData={setSavedData}
        savedDataArray={savedDataArray}
        setSavedDataArray={setSavedDataArray}
        setChecked={setChecked}
        checked={checked}
        ntlSup={ntlSup}
        ntlYoy={ntlYoy}
        ntlAffor={ntlAffor}
        ntlPop={ntlPop}
      />
      <div className="contents">
        {showHowToInfo ? (
          <HowToPopUp
            showHowToInfo={showHowToInfo}
            howtoRef={howtoRef}
            handleClickClose={handleClickClose}
          />
        ) : (
          <></>
        )}
        <SelectState
          handleClickArea={handleClickArea}
          currentData={currentData}
          selectState={selectState}
          handleClickSelectState={handleClickSelectState}
        />
        {showStates ? (
          <div className="selectState__close mobile-only">
            <p
              className="selectState__close__text"
              onClick={() => {
                handleClickSelectState('close');
              }}
            >
              close
            </p>
          </div>
        ) : (
          <></>
        )}

        <div className="app__hamburger  mobile-only">
          {savedDataArray.length > 0 ? (
            <div className="compare__circle compare__circle--hamburger">
              <img className="compare__circle__circle" src={compareCircle} alt="" />
              <p className="compare__circle__number">{savedDataArray.length}</p>
            </div>
          ) : (
            <></>
          )}
          <img
            className="app__hamburger__icon"
            src={hamburger}
            alt="menu"
            onClick={
              showHamburger
                ? () => {
                    handleClickHamburger('close');
                  }
                : () => {
                    handleClickHamburger('open');
                  }
            }
          />
        </div>
        <Hamburger
          showHamburger={showHamburger}
          setShowHamburger={setShowHamburger}
          hamburgerRef={hamburgerRef}
          setShowAreaDetail={setShowAreaDetail}
          setShowCompare={setShowCompare}
          handleClickHamburger={handleClickHamburger}
          savedDataArray={savedDataArray}
          introText={introText}
          handleClickI={handleClickI}
          setCurrentState={setCurrentState}
          handleClickNavButtons={handleClickNavButtons}
          buttonActive={buttonActive}
        />
        <a
          href="https://www.realtor.com/thats-who-we-r/"
          target="_blank"
          rel="sponsored noopener"
          className="nar"
        >
          <img
            className="logo desktop-only"
            src={logo}
            alt="Logo of National Association Of Realtors"
            ref={logoRef}
            onMouseEnter={(e) => {
              handleMouseEnterLogo(e);
            }}
            onMouseLeave={handleMouseLeaveLogo}
          />
        </a>
        <div className="navTooltip navTooltip-logo desktop-only" ref={logoTooltip}>
          <p className="navTooltip-content">Visit www.realtor.com/thats-who-we-r/</p>
        </div>
        {showOverview ? (
          <img
            className="plus desktop-only"
            src={plus}
            alt=""
            ref={plusRef}
            onClick={handleClickCollapse}
          />
        ) : (
          <></>
        )}

        <div className="nav-gradient" />
        <div className="navRight desktop-only" ref={rightNav}>
          <NavRight
            savedDataArray={savedDataArray}
            setShowAreaDetail={setShowAreaDetail}
            setShowCompare={setShowCompare}
            handleReset={handleReset}
            handleClickI={handleClickI}
          />
        </div>
        <div ref={topNav} className="topnav">
          <NavButtons
            handleClickNavButtons={handleClickNavButtons}
            buttonActive={buttonActive}
            handleClickI={handleClickI}
            setShowHowToInfo={setShowHowToInfo}
            exploreTheStates={exploreTheStates}
            supplyRef={supplyRef}
            appRef={appRef}
            affRef={affRef}
            forecastRef={forecastRef}
            mobileView={mobileView}
          />
        </div>
        <div className="frame" ref={frame}>
          <SelectStateButton
            handleClickSelectState={handleClickSelectState}
            selectStateButtonRef={selectStateButtonRef}
            showStates={showStates}
            loc="top"
          />
          <Container
            handleClickNavButtons={handleClickNavButtons}
            currentArea={currentArea}
            setCurrentArea={setCurrentArea}
            currentData={currentData}
            colorCode={colorCode}
            handleClickMetroArea={handleClickMetroArea}
            northAm={northAm}
            zoomed={zoomed}
            filler={filler}
            currentClickedArea={currentClickedArea}
            currentState={currentState}
            currentStateName={currentStateName}
            stateLines={stateLines}
            mapContainer={mapContainer}
            stateMap={stateMap}
            buttonActive={buttonActive}
            handleClickIntroState={handleClickIntroState}
          />

          {showAreaDetail && !mobileView ? (
            <AreaDetail
              currentClickedArea={currentClickedArea}
              setSavedData={setSavedData}
              savedData={savedData}
              checked={checked}
              setChecked={setChecked}
              checkbox={checkbox}
              setShowAreaDetail={setShowAreaDetail}
              setSavedDataArray={setSavedDataArray}
              setCurrentClickedArea={setCurrentClickedArea}
              areaDetailRef={areaDetailRef}
            />
          ) : (
            <></>
          )}
          {showStateDetail && !mobileView && !showAreaDetail ? (
            <StateDetail
              setShowStateDetail={setShowStateDetail}
              selectedStateIntro={selectedStateIntro}
              setSelectedStateIntro={setSelectedStateIntro}
              handleClickNavButtons={handleClickNavButtons}
              currentStateName={currentStateName}
            />
          ) : (
            <></>
          )}

          {currentState === null ? (
            <div className="colorCode">
              {buttonActive === 'supply' ? (
                <>
                  <ColorKeys colorKeyContent={colorKeyContent.supply} mobileView={mobileView} />
                </>
              ) : (
                <></>
              )}
              {buttonActive === 'appreciation' ? (
                <>
                  <ColorKeys
                    colorKeyContent={colorKeyContent.appreciation}
                    mobileView={mobileView}
                  />
                </>
              ) : (
                <></>
              )}
              {buttonActive === 'affordability' ? (
                <>
                  <ColorKeys
                    colorKeyContent={colorKeyContent.affordability}
                    mobileView={mobileView}
                  />
                </>
              ) : (
                <></>
              )}
              {buttonActive === 'forecast' ? (
                <>
                  <ColorKeys colorKeyContent={colorKeyContent.forecast} mobileView={mobileView} />
                </>
              ) : (
                <></>
              )}
            </div>
          ) : (
            <></>
          )}
          <CategoryOverview
            currentCategory={currentCategory}
            overviewCopy={overviewCopy}
            overview={overview}
            showOverview={showOverview}
            handleClickCollapse={handleClickCollapse}
            collapseRef={collapseRef}
          />
          <AreaDetailMobile
            currentState={currentState}
            setSavedData={setSavedData}
            savedData={savedData}
            checked={checked}
            setChecked={setChecked}
            checkbox={checkbox}
            setShowAreaDetail={setShowAreaDetail}
            setSavedDataArray={setSavedDataArray}
            currentStateName={currentStateName}
            setCurrentState={setCurrentState}
            handleClickNavButtons={handleClickNavButtons}
            buttonActive={buttonActive}
            ntlSup={ntlSup}
            ntlMedPrice={ntlMedPrice}
            ntlYoy={ntlYoy}
            ntlAffor={ntlAffor}
            ntlPop={ntlPop}
          />
          {currentState ? (
            <SelectStateNav
              showStates={showStates}
              handleClickSelectState={handleClickSelectState}
              selectStateButtonRef={selectStateButtonRef}
              currentStateIndex={currentStateIndex}
              handleClickArea={handleClickArea}
              previousStateName={previousStateName}
              nextStateName={nextStateName}
            />
          ) : (
            <></>
          )}
          <p className="app__disclaimer app__disclaimer--mobile">
            REALTORS® are members of the National Association of REALTORS®.
          </p>
        </div>

        <p className="app__disclaimer app__disclaimer--desktop" ref={disclaimerRef}>
          REALTORS® are members of the National Association of REALTORS®.
        </p>
      </div>
    </section>
  );
}

export default App;
